import { Menu, X } from 'lucide-react';
import { lazy, Suspense, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { NavItem } from '@/components/ui/dropdown-navigation';
import Logo from '@/components/ui/logo';
import { ROUTES } from '@/constants/routes';
import { usePublicAuthStatus } from '@/hooks/use-public-auth-status';
import { cn } from '@/lib/tw-merge/cn';
import { isRouteAccessibleInCurrentEnvironment } from '@/utils/prelaunch-route-access';

import type { MobileNavItem } from './mobile-nav-drawer';

const DropdownNavigation = lazy(() =>
  import('@/components/ui/dropdown-navigation').then(module => ({
    default: module.DropdownNavigation,
  }))
);
const UserDropdown = lazy(() =>
  import('@/components/ui/user-dropdown').then(module => ({
    default: module.UserDropdown,
  }))
);
const AgentsMenuContent = lazy(() => import('./agents-menu-content'));
const CompanyMenuContent = lazy(() => import('./company-menu-content'));
const MobileNavDrawer = lazy(() => import('./mobile-nav-drawer'));

interface NavbarProps {
  /**
   * `dark` (default) — white text + white logo, designed to overlay a
   * dark hero. `light` — black text + brand logo for pages with a
   * light background (e.g. pricing).
   */
  theme?: 'dark' | 'light';
  hideGetStarted?: boolean;
  containerClassName?: string;
}

export default function Navbar({
  theme = 'dark',
  hideGetStarted = false,
  containerClassName = 'app-container',
}: NavbarProps) {
  const publicAuth = usePublicAuthStatus();
  const { isAuthenticated, hasResolved, login } = publicAuth;
  const [isOpen, setIsOpen] = useState(false);
  const isLight = theme === 'light';

  // Desktop nav: drives both the dropdown skeleton and the underline animation.
  // Add new items here; if one needs a dropdown, set `subMenus` (icon-grid) or
  // `renderContent` (fully custom panel). Anything without either renders as a
  // plain link.
  const desktopNavItems = useMemo(() => {
    const all: NavItem[] = [
      {
        id: 1,
        label: 'Agents',
        renderContent: ({ close }) => (
          <Suspense fallback={<NavPanelFallback />}>
            <AgentsMenuContent close={close} />
          </Suspense>
        ),
      },
      { id: 2, label: 'See It Run', link: ROUTES.SEE_IT_RUN },
      { id: 3, label: 'Integrations', link: ROUTES.INTEGRATIONS },
      { id: 4, label: 'Meet Live AI', link: ROUTES.MEET_LIVE_AI },
      { id: 5, label: 'Pricing', link: ROUTES.PRICING },
      {
        id: 6,
        label: 'Company',
        dropdownAlign: 'trigger-right',
        renderContent: ({ close }) => (
          <Suspense fallback={<NavPanelFallback compact />}>
            <CompanyMenuContent close={close} />
          </Suspense>
        ),
      },
    ];
    return all.filter(item =>
      item.link ? isRouteAccessibleInCurrentEnvironment(item.link) : true
    );
  }, []);

  // Mobile drawer items — accordion-expandable rows. Agents gets its
  // subItems injected from the cached suites query inside MobileNavDrawer
  // (so this list doesn't need to know about suite data). Company has
  // static subItems matching its desktop dropdown.
  const mobileItems = useMemo<MobileNavItem[]>(() => {
    const all: MobileNavItem[] = [
      { id: 1, label: 'Agents' },
      { id: 2, label: 'Meet Live AI', link: ROUTES.MEET_LIVE_AI },
      { id: 3, label: 'See It Run', link: ROUTES.SEE_IT_RUN },
      { id: 4, label: 'Integrations', link: ROUTES.INTEGRATIONS },
      { id: 5, label: 'Pricing', link: ROUTES.PRICING },
      {
        id: 6,
        label: 'Company',
        subItems: [
          { label: 'Our Story', href: ROUTES.OUR_STORY },
          { label: 'The Ledger', href: ROUTES.BLOG_LEDGER },
          { label: 'The Inference', href: ROUTES.BLOG_INFERENCE },
          { label: 'Contact', href: ROUTES.CONTACT },
        ].filter(link => isRouteAccessibleInCurrentEnvironment(link.href)),
      },
    ];
    return all.filter(item =>
      item.link ? isRouteAccessibleInCurrentEnvironment(item.link) : true
    );
  }, []);

  // Anchors the dropdown panel: it portals into this container so it spans
  // the navbar's full width instead of centering on the trigger.
  const navContainerRef = useRef<HTMLDivElement>(null);

  return (
    <nav className="absolute left-0 top-0 z-50 w-full py-8">
      <div
        ref={navContainerRef}
        className={cn(
          'relative flex items-center justify-between',
          containerClassName,
          isLight ? 'text-foreground' : 'text-white'
        )}
      >
        <div className="flex items-center">
          <Logo theme={isLight ? 'brand' : 'white'} />
        </div>

        <div className="hidden lg:block">
          <Suspense
            fallback={
              <PlainDesktopNav navItems={desktopNavItems} isLight={isLight} />
            }
          >
            <DropdownNavigation
              navItems={desktopNavItems}
              theme={isLight ? 'light' : 'dark'}
              panelContainer={navContainerRef}
            />
          </Suspense>
        </div>

        <div className="hidden items-center gap-6 lg:flex">
          {!hasResolved ? (
            <div className="h-10 w-[168px]" aria-hidden="true" />
          ) : isAuthenticated ? (
            <Suspense fallback={<div className="size-10" aria-hidden="true" />}>
              <UserDropdown
                userOverride={{
                  firstName: publicAuth.firstName,
                  lastName: publicAuth.lastName,
                  email: publicAuth.email,
                  profilePicture: publicAuth.picture,
                }}
                logoutOverride={publicAuth.logout}
              />
            </Suspense>
          ) : (
            <>
              <button
                onClick={() => login()}
                className="text-sm font-medium transition-opacity hover:opacity-80"
              >
                Login
              </button>
              {!hideGetStarted && (
                <Link
                  to={ROUTES.SIGNUP}
                  className="rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
                >
                  Get Started
                </Link>
              )}
            </>
          )}
        </div>

        <button
          type="button"
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isOpen}
          onClick={() => setIsOpen(o => !o)}
          className={cn(
            'relative z-[60] lg:hidden',
            isLight ? 'text-gray-900' : 'text-white'
          )}
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {isOpen && (
        <Suspense fallback={null}>
          <MobileNavDrawer
            open
            onClose={() => setIsOpen(false)}
            items={mobileItems}
            hideGetStarted={hideGetStarted}
            publicAuth={publicAuth}
          />
        </Suspense>
      )}
    </nav>
  );
}

function PlainDesktopNav({
  navItems,
  isLight,
}: {
  navItems: NavItem[];
  isLight: boolean;
}) {
  return (
    <ul className="flex items-center gap-8">
      {navItems.map(item =>
        item.link ? (
          <li key={item.id}>
            <Link
              to={item.link}
              className={cn(
                'text-sm font-medium transition-opacity hover:opacity-80',
                isLight ? 'text-foreground-secondary' : 'text-white/90'
              )}
            >
              {item.label}
            </Link>
          </li>
        ) : (
          <li
            key={item.id}
            className={cn(
              'text-sm font-medium',
              isLight ? 'text-foreground-secondary' : 'text-white/90'
            )}
          >
            {item.label}
          </li>
        )
      )}
    </ul>
  );
}

function NavPanelFallback({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-3xl bg-white',
        compact ? 'h-40 w-56' : 'h-[360px] w-[min(1307px,calc(100vw-48px))]'
      )}
      aria-hidden="true"
    />
  );
}
